import React, { memo, useCallback, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { createPortal } from "react-dom";

const closeIcon = icon({ name: "xmark", family: "sharp", style: "regular" });
const calendarIcon = icon({ name: "calendar-days", family: "sharp", style: "regular" });

/**
 * ConstraintChip - A chip component for displaying filter constraints
 * 
 * Displays in "category: summary" format with a remove button on hover.
 * Click opens the filter sidebar, X removes the filter.
 * Shows a preview tooltip on hover when multiple values are selected.
 */
const ConstraintChip = memo(({ category, summary, selectedValues, onClick, onRemove, isLocked = false, isTimePeriod = false }) => {
	const [isHovered, setIsHovered] = useState(false);
	const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
	const chipRef = useRef(null);
	const tooltipRef = useRef(null);
	const hideTimeoutRef = useRef(null);

	// Check if we should show the preview (multiple values selected)
	const showPreview = selectedValues && selectedValues.length > 1;

	// Update tooltip position when hovered
	useEffect(() => {
		if (!isHovered || !chipRef.current || !showPreview) return;

		const updatePosition = () => {
			if (!chipRef.current) return;

			const chipRect = chipRef.current.getBoundingClientRect();
			const scrollY = window.scrollY || window.pageYOffset;
			const scrollX = window.scrollX || window.pageXOffset;
			const viewportWidth = window.innerWidth;
			const viewportHeight = window.innerHeight;

			// Estimate tooltip dimensions (max-width: 24rem = 384px, typical height for list)
			const estimatedTooltipWidth = 384;
			const estimatedTooltipHeight = Math.min(300, (selectedValues?.length || 0) * 24 + 60); // ~24px per item + header

			// Default: position below the chip, aligned to the left
			let top = chipRect.bottom + scrollY + 8; // 8px gap below chip
			let left = chipRect.left + scrollX;

			// Adjust if tooltip would go off the right edge
			if (left + estimatedTooltipWidth > scrollX + viewportWidth) {
				left = Math.max(scrollX + 16, scrollX + viewportWidth - estimatedTooltipWidth - 16);
			}

			// Adjust if tooltip would go off the left edge
			if (left < scrollX + 16) {
				left = scrollX + 16; // 16px margin from edge
			}

			// Adjust if tooltip would go off the bottom edge (show above instead)
			if (chipRect.bottom + estimatedTooltipHeight + 8 > viewportHeight) {
				top = chipRect.top + scrollY - estimatedTooltipHeight - 8; // 8px gap above chip
				// Ensure it doesn't go above viewport
				if (top < scrollY + 16) {
					top = scrollY + 16;
				}
			}

			setTooltipPosition({ top, left });
		};

		// Initial position calculation
		updatePosition();

		// Update position on scroll/resize
		window.addEventListener('scroll', updatePosition, true);
		window.addEventListener('resize', updatePosition);

		return () => {
			window.removeEventListener('scroll', updatePosition, true);
			window.removeEventListener('resize', updatePosition);
		};
	}, [isHovered, showPreview, selectedValues?.length]);

	const handleClick = useCallback((e) => {
		if (isLocked) {
			e.preventDefault();
			e.stopPropagation();
			return;
		}
		e.stopPropagation();
		onClick?.();
	}, [onClick, isLocked]);

	const handleRemove = useCallback((e) => {
		e.stopPropagation();
		onRemove?.();
	}, [onRemove]);

	const handleKeyDown = useCallback((e) => {
		if (isLocked) {
			e.preventDefault();
			return;
		}
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			onClick?.();
		}
	}, [onClick, isLocked]);

	const handleRemoveKeyDown = useCallback((e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			e.stopPropagation();
			onRemove?.();
		}
	}, [onRemove]);

	const handleMouseEnter = useCallback(() => {
		if (showPreview) {
			// Clear any pending hide timeout
			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
				hideTimeoutRef.current = null;
			}
			setIsHovered(true);
		}
	}, [showPreview]);

	const handleMouseLeave = useCallback(() => {
		// Add small delay before hiding to allow moving mouse to tooltip
		hideTimeoutRef.current = setTimeout(() => {
			setIsHovered(false);
			hideTimeoutRef.current = null;
		}, 100);
	}, []);

	const handleTooltipMouseEnter = useCallback(() => {
		// Clear any pending hide timeout
		if (hideTimeoutRef.current) {
			clearTimeout(hideTimeoutRef.current);
			hideTimeoutRef.current = null;
		}
		setIsHovered(true);
	}, []);

	const handleTooltipMouseLeave = useCallback(() => {
		setIsHovered(false);
	}, []);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
			}
		};
	}, []);

	// Time period chips get special styling
	const chipClasses = isTimePeriod
		? `group inline-flex items-center bg-primary-soft rounded-full px-3 py-1.5 text-sm transition-colors duration-150 border border-primary relative ${
			isLocked
				? 'cursor-default'
				: 'hover:bg-primary-soft cursor-pointer'
		}`
		: `group inline-flex items-center bg-surface-2 rounded-full px-3 py-1.5 text-sm transition-colors duration-150 border-none relative ${
			isLocked
				? 'cursor-default opacity-75'
				: 'hover:bg-surface-3 cursor-pointer'
		}`;

	return (
		<>
			<button
				ref={chipRef}
				type="button"
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				className={chipClasses}
				aria-label={isLocked 
					? `Filter: ${category}: ${summary}. Locked in view mode.` 
					: `Filter: ${category}: ${summary}. Click to edit.`}
				aria-disabled={isLocked}
				data-constraint-chip
			>
				{isTimePeriod && (
					<FontAwesomeIcon
						icon={calendarIcon}
						className="text-primary text-xs mr-1.5"
					/>
				)}
				<span className={isTimePeriod ? "text-primary font-normal" : "text-text-2 font-normal"}>{category}:</span>
				<span className={`ml-1 font-medium truncate max-w-[150px] ${isTimePeriod ? "text-primary" : "text-text"}`} title={summary}>
					{summary}
				</span>
				{!isLocked && onRemove && (
					<span
						role="button"
						tabIndex={0}
						onClick={handleRemove}
						onKeyDown={handleRemoveKeyDown}
						className={`ml-2 w-4 h-4 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-150 cursor-pointer ${
							isTimePeriod ? "hover:bg-primary-soft" : "hover:bg-border"
						}`}
						aria-label={`Remove ${category} filter`}
					>
						<FontAwesomeIcon
							icon={closeIcon}
							className={isTimePeriod ? "text-primary text-xs" : "text-text-2 text-xs"}
						/>
					</span>
				)}
			</button>

			{/* Preview tooltip - only shown when multiple values are selected and hovered */}
			{isHovered && showPreview && createPortal(
				<div
					ref={tooltipRef}
					className="fixed cursor-default bg-card border border-card-bd rounded-card shadow-float z-[10000] max-w-sm"
					style={{
						top: `${tooltipPosition.top}px`,
						left: `${tooltipPosition.left}px`,
						pointerEvents: 'auto',
					}}
					onMouseEnter={handleTooltipMouseEnter}
					onMouseLeave={handleTooltipMouseLeave}
				>
					<div className="p-3">
						{/* First line: Category and count */}
						<div className="text-sm font-medium text-text mb-2 pb-2 border-b border-border">
							{category}: {selectedValues.length} selected
						</div>
						{/* List of selected values */}
						<div className="max-h-64 overflow-y-auto">
							<ul className="space-y-1">
								{selectedValues.map((value, index) => (
									<li
										key={`${value}-${index}`}
										className="text-sm text-text-2 truncate"
										title={value}
									>
										{value}
									</li>
								))}
							</ul>
						</div>
					</div>
				</div>,
				document.body
			)}
		</>
	);
});

ConstraintChip.displayName = 'ConstraintChip';

ConstraintChip.propTypes = {
	/** The category/title of the constraint */
	category: PropTypes.string.isRequired,
	/** The summary text (single value or "x selected") */
	summary: PropTypes.string.isRequired,
	/** Array of selected values - used to show preview tooltip when multiple values are selected */
	selectedValues: PropTypes.arrayOf(PropTypes.string),
	/** Click handler to open the filter sidebar */
	onClick: PropTypes.func,
	/** Handler to remove the filter */
	onRemove: PropTypes.func,
	/** Whether the chip is locked (disabled) - hides remove button and disables click */
	isLocked: PropTypes.bool,
	/** Whether this is a time period chip (special styling) */
	isTimePeriod: PropTypes.bool,
};

export default ConstraintChip;
